<!--
  Product disclaimer stack. Presentational, prop-driven — renders the per-product
  caution/shipping disclaimers. Extracted from storefront/product.vue as a
  Pinia-free decomposition step.

  Two wrappers, matching the legacy markup exactly:
   1. The pre-launch caution wrapper — always present, self-toggled via its own
      `active` class (onboarding pre-order creator view).
   2. The stock/live wrapper — gated by `showStockLive` (the parent's
      `(product.isStock && !product.isSoldOut && !disabledLegacyOnDemand) ||
      product.isLive` expression). Each `.product-disclaimer` inside is self-
      toggled via `active`.

  The two "size chart" links emit `view-size-chart` (the parent owns the
  Velocity scroll to `.nylon-ApparelResources`). Render-neutral; `.product-disclaimer*`
  classes preserved (styled by the product partials / skinned).
-->
<!--
  Two sibling root nodes (Vue 3 fragment) — NOT wrapped in an extra <div> — to
  preserve the exact legacy DOM (the wrappers are direct siblings of `.buy` etc.
  in the parent's `.choices` container).
-->
<template>
  <div class="product-disclaimer-wrapper">
    <div
      class="product-disclaimer product-disclaimer--caution"
      :class="{
        active: product.publishingStatus === 'onboarding' && product.isPreOrder,
      }"
    >
      <figure>
        <span>!</span>
      </figure>
      <div class="figcaption">
        <p>
          This campaign product is currently
          <strong>pre-launch</strong>. As the logged-in creator of this product, you are the only person who can see it.
        </p>
      </div>
    </div>
  </div>

  <div v-if="showStockLive" class="product-disclaimer-wrapper">
    <div
      class="product-disclaimer"
      :class="{
        active: selectedVariant.isBackorder || [110262, 110263].includes(product.id),
      }"
    >
      <figure>
        <span>!</span>
      </figure>
      <div class="figcaption">
        <p>This item is currently in production.</p>
      </div>
    </div>

    <div
      class="product-disclaimer"
      :class="{
        active: selectedVariant.manufacturer === 'gildan' && selectedVariant.code === '2000',
      }"
    >
      <figure>
        <span>!</span>
      </figure>
      <div class="figcaption">
        <p>
          This size is printed on a heavyweight Gildan tee. Please check our
          <a @click.prevent="$emit('view-size-chart')" href="#">size chart and product details</a>
          for more information.
        </p>
      </div>
    </div>

    <div
      class="product-disclaimer"
      :class="{
        active: selectedVariant.manufacturer === 'next level' && selectedVariant.sex === 'women',
      }"
    >
      <figure>
        <span>!</span>
      </figure>
      <div class="figcaption">
        <p>
          Our junior fit tees fit like, well, junior fit tees. Please check our
          <a @click.prevent="$emit('view-size-chart')" href="#">size chart</a>
          to ensure a good fit.
        </p>
      </div>
    </div>

    <div class="product-disclaimer" :class="{ active: selectedVariant.productId == 21145 }">
      <figure>
        <span>!</span>
      </figure>
      <div class="figcaption">
        <p>This product is not available for international shipping. Standard discount codes may not apply.</p>
      </div>
    </div>

    <div
      class="product-disclaimer holiday-shipping-yes"
      :class="{
        active: product.willArriveByChristmas !== null && product.willArriveByChristmas,
      }"
    >
      <figure>
        <NylonIcon icon-name="tree" classes="nylon-Icon--colorDarkGreen" :has-texture="true" />
      </figure>
      <div class="figcaption">
        <p>
          <strong>Get it in time for Christmas!</strong>
          There’s an excellent chance this product arrives before Dec. 25. (U.S. customers only.)
        </p>
      </div>
    </div>

    <div
      class="product-disclaimer holiday-shipping-no"
      :class="{
        active: product.willArriveByChristmas !== null && !product.willArriveByChristmas,
      }"
    >
      <figure>
        <NylonIcon icon-name="tree" classes="nylon-Icon--colorDarkRed" :has-texture="true" />
      </figure>
      <div class="figcaption">
        <p>
          <strong>Warning!</strong>
          This product is subject to our holiday shipping schedule. Items may not arrive before Dec. 25.
        </p>
      </div>
    </div>

    <div
      class="product-disclaimer product-disclaimer--caution"
      :class="{
        active: product.publishingStatus === 'unpublished',
      }"
    >
      <figure>
        <span>!</span>
      </figure>
      <div class="figcaption">
        <p>
          This product is currently
          <strong>unpublished</strong>. As the logged-in creator of this product, you are the only person who can see and purchase it.
        </p>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "ProductDisclaimers",
  emits: ["view-size-chart"],

  props: {
    product: { type: Object, required: true },
    selectedVariant: { type: Object, required: true },
    // The parent's stock/live gate; also gates `.buy`/stats/nylon-CampaignInfo there.
    showStockLive: { type: Boolean, default: false },
  },
};
</script>
